import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV } from "@/lib/content";
import { Logo } from "./Logo";
import { BookButton } from "./Buttons";
import { ease } from "./motion";

const MobileMenu = ({ onClose }) => (
  <motion.div
    data-testid="mobile-menu"
    className="fixed inset-0 z-[60] bg-[#082F24] grain flex flex-col px-6 pt-6 pb-10"
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.7, ease }}
  >
    <div className="flex items-center justify-between">
      <Logo onClick={onClose} testId="mobile-logo-link" />
      <button data-testid="mobile-menu-close-btn" onClick={onClose} aria-label="Close menu" className="grid h-11 w-11 place-items-center rounded-full border border-[#FAF8F5]/25 text-[#FAF8F5]">
        <X className="h-5 w-5" />
      </button>
    </div>
    <nav className="mt-16 flex flex-col gap-2">
      {NAV.map((n, i) => (
        <motion.div key={n.to} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.07, duration: 0.7, ease }}>
          <NavLink to={n.to} onClick={onClose} data-testid={`mobile-nav-${n.label.toLowerCase()}`} className={({ isActive }) => `font-display text-5xl tracking-tight ${isActive ? "text-[#D4AF37] italic" : "text-[#FAF8F5]"}`}>
            {n.label}
          </NavLink>
        </motion.div>
      ))}
    </nav>
    <div className="mt-auto"><BookButton testId="mobile-book-consultation-btn" /></div>
  </motion.div>
);

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      <header data-testid="main-navbar" className="fixed top-0 inset-x-0 z-50 px-3 sm:px-6 pt-3 sm:pt-4">
        <div className={`mx-auto max-w-7xl flex items-center justify-between rounded-full pl-5 pr-2 py-2 transition-[background-color,box-shadow] duration-500 ${scrolled ? "bg-[#082F24]/85 backdrop-blur-xl shadow-[0_10px_40px_-10px_rgba(8,47,36,0.5)]" : "bg-[#082F24]/40 backdrop-blur-md"}`}>
          <Logo />
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end data-testid={`nav-${n.label.toLowerCase()}`} className={({ isActive }) => `relative px-4 py-2 text-sm rounded-full transition-colors duration-300 ${isActive ? "text-[#082F24] bg-[#FAF8F5]" : "text-[#FAF8F5]/85 hover:text-[#FAF8F5] hover:bg-[#FAF8F5]/10"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:block"><BookButton testId="nav-book-consultation-btn">Book a call</BookButton></div>
          <button data-testid="mobile-menu-open-btn" onClick={() => setOpen(true)} aria-label="Open menu" className="lg:hidden grid h-11 w-11 place-items-center rounded-full bg-[#D4AF37] text-[#082F24]">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
